import { markdown } from "@codemirror/lang-markdown";
import { EditorView, type Extension, keymap, Prec } from "@uiw/react-codemirror";
import { EDITOR_SURFACE } from "../../common/editor-theme";
import { formatEdit, type FormatKind } from "./format";
import { FORMAT_BUTTONS } from "./toolbar";

// Dispatched rather than fed back as a value, which keeps a format on the editor's own undo stack: a button and the
// typing around it are the same kind of edit, and one Ctrl+Z takes either back.
export function applyFormat(view: EditorView | null, kind: FormatKind) {
  if (!view) return;

  const { from, to } = view.state.selection.main;
  const edit = formatEdit(kind, view.state.doc.toString(), from, to);
  view.dispatch({
    changes: { from: edit.from, to: edit.to, insert: edit.insert },
    selection: { anchor: edit.selection[0], head: edit.selection[1] },
    scrollIntoView: true,
  });
  // The click took the focus off the document, and what the format left selected is meant to be typed over.
  view.focus();
}

// One transaction for the whole document, which puts an opened file on the undo stack beside the typing: Ctrl+Z is
// then what puts back what was being written, and the caret starts at the top of what arrived.
export function replaceDocument(view: EditorView | null, text: string) {
  if (!view) return;
  view.dispatch({
    changes: { from: 0, to: view.state.doc.length, insert: text },
    selection: { anchor: 0 },
    scrollIntoView: true,
  });
}

// Registered handlers run before CodeMirror's own, and a `true` is what keeps them from running: dropping a file is
// opening it here, where CodeMirror would read it in at the caret and leave one document inside another.
export function fileDropHandlers(
  onDragging: (dragging: boolean) => void,
  onFile: (file: File) => void,
): Extension {
  return EditorView.domEventHandlers({
    dragover: (event) => {
      if (!hasFile(event)) return false;
      event.preventDefault();
      onDragging(true);
      return true;
    },
    dragleave: (event, view) => {
      if (!view.dom.contains(event.relatedTarget as Node | null)) onDragging(false);
      return false;
    },
    drop: (event) => {
      onDragging(false);
      const file = hasFile(event) ? event.dataTransfer?.files.item(0) : null;
      if (!file) return false;
      event.preventDefault();
      onFile(file);
      return true;
    },
  });
}

// A drag carries what it is before it carries what it holds, so the type list is the only thing there is to read
// while one is still over the editor.
function hasFile(event: DragEvent) {
  return event.dataTransfer?.types.includes("Files") ?? false;
}

// Above the default keymap, which has its own claim on some of these strokes.
const FORMAT_KEYMAP = Prec.high(keymap.of(
  FORMAT_BUTTONS.flatMap(({ kind, key }) =>
    key
      ? [{
        key,
        run: (view: EditorView) => {
          applyFormat(view, kind);
          return true;
        },
      }]
      : []
  ),
));

// Out here so their identity never changes: a new array reconfigures the running editor.
export const EDITOR_EXTENSIONS = [
  markdown(),
  FORMAT_KEYMAP,
  // Prose is written in paragraphs rather than in lines, so a long one wraps instead of scrolling sideways.
  EditorView.lineWrapping,
  ...EDITOR_SURFACE,
];
